import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import {
  forwardRef,
  type KeyboardEvent,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createEditor, type Descendant, Editor, Range, Transforms } from "slate";
import { withHistory } from "slate-history";
import { Editable, Slate, withReact } from "slate-react";
import type {
  AtMentionState,
  CustomElement,
  CustomText,
  RichEditorProps,
  RichEditorRef,
} from "@/interfaces/CampaignBuilder";
import type { TokenEmail } from "@/interfaces/emailPreview";
import type { TextFormat } from "@/types/types";
import {
  deserializeHtml,
  getPlainLength,
  HOTKEYS,
  matchesHotkey,
  serializeHtml,
  toggleMark,
} from "./html-helper";
import { AlignButton, Element, Leaf, MarkButton } from "./RichEditorComponents";
import { TokenMenu } from "./TokenMenu";

declare module "slate" {
  interface CustomTypes {
    Element: CustomElement;
    Text: CustomText;
  }
}

// ── At-mention state ───────────────────────────────────────────────────────

const INITIAL_AT_MENTION: AtMentionState = {
  active: false,
  startPoint: null,
  items: [],
  activeIdx: 0,
};

// ── RichEditor ─────────────────────────────────────────────────────────────

const RichEditor = forwardRef<RichEditorRef, RichEditorProps>(function RichEditor(
  {
    initialHtml = "",
    onChange,
    maxLength,
    minHeight = "120px",
    placeholder,
    atMentionTokens,
    showAlign = false,
  },
  ref,
) {
  // Stable ref so the plugin closure always reads the current maxLength
  const maxLengthRef = useRef(maxLength);
  maxLengthRef.current = maxLength;

  const editor = useMemo(() => {
    const e = withHistory(withReact(createEditor()));

    // Plugin: clamp insertions at maxLength before they enter the editor.
    // This is the only safe place to enforce the limit — trying to revert
    // inside onChange crashes because Slate's operations list still points
    // to paths that no longer exist after a forced children replacement.
    const { insertText, insertData } = e;

    const getRemaining = (): number | undefined => {
      const limit = maxLengthRef.current;
      return limit !== undefined ? limit - getPlainLength(e.children) : undefined;
    };

    e.insertText = (text) => {
      const remaining = getRemaining();
      if (remaining === undefined) {
        insertText(text);
        return;
      }
      if (remaining <= 0) return;
      insertText(remaining < text.length ? text.slice(0, remaining) : text);
    };

    e.insertData = (data: DataTransfer) => {
      const remaining = getRemaining();
      if (remaining === undefined) {
        insertData(data);
        return;
      }
      const plain = data.getData("text/plain");
      if (remaining <= 0) return;
      // If the pasted plain text fits, let Slate handle it normally (preserves
      // rich-text formatting). Otherwise, insert the truncated plain text.
      if (plain.length <= remaining) {
        insertData(data);
      } else {
        insertText(plain.slice(0, remaining));
      }
    };

    Editor.normalize(e, { force: true });
    return e;
  }, []);

  const initialValue = useMemo(() => deserializeHtml(initialHtml), [initialHtml]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // at-mention state — use a ref alongside state so handleChange always reads fresh values
  const [atMention, setAtMentionState] = useState<AtMentionState>(INITIAL_AT_MENTION);
  const atMentionRef = useRef<AtMentionState>(INITIAL_AT_MENTION);
  function setAtMention(next: AtMentionState) {
    atMentionRef.current = next;
    setAtMentionState(next);
  }

  useImperativeHandle(ref, () => ({
    insertText(text: string) {
      editor.insertText(text);
    },
  }));

  const renderLeaf = useCallback(
    (props: { attributes: object; children: React.ReactNode; leaf: CustomText }) => (
      <Leaf {...props} />
    ),
    [],
  );
  const renderElement = useCallback(
    (props: { attributes: object; children: React.ReactNode; element: CustomElement }) => (
      <Element {...props} />
    ),
    [],
  );

  function hideAtMention() {
    setAtMention(INITIAL_AT_MENTION);
  }

  function insertAtMentionToken(token: TokenEmail) {
    const { startPoint } = atMentionRef.current;
    if (!startPoint || !editor.selection) return;
    const cursor = editor.selection.anchor;
    Transforms.select(editor, { anchor: startPoint, focus: cursor });
    Transforms.insertText(editor, token.tag + " ");
    hideAtMention();
  }

  function handleChange(value: Descendant[]) {
    const html = serializeHtml(value);
    // No revert needed — the insertText/insertData plugin prevents over-limit content.
    onChange(html);

    // @-mention detection
    if (!atMentionTokens?.length) return;
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) {
      if (atMentionRef.current.active) hideAtMention();
      return;
    }
    const cursor = selection.anchor;
    const [node] = Editor.node(editor, cursor.path);
    const nodeText = (node as CustomText).text ?? "";
    const am = atMentionRef.current;

    if (!am.active) {
      if (cursor.offset > 0 && nodeText[cursor.offset - 1] === "@") {
        setAtMention({
          active: true,
          startPoint: { path: cursor.path, offset: cursor.offset - 1 },
          items: atMentionTokens,
          activeIdx: 0,
        });
      }
    } else {
      // Must still be in the same text node and past startOffset
      if (
        !am.startPoint ||
        cursor.path[0] !== am.startPoint.path[0] ||
        cursor.path[1] !== am.startPoint.path[1] ||
        cursor.offset <= am.startPoint.offset
      ) {
        hideAtMention();
        return;
      }
      const query = nodeText.slice(am.startPoint.offset + 1, cursor.offset);
      if (/\s/.test(query)) {
        hideAtMention();
        return;
      }
      const q = query.toLowerCase();
      const filtered = atMentionTokens.filter((t) => {
        const tagLow = t.tag.slice(1).toLowerCase();
        const labelLow = t.label.toLowerCase().replace(/\s+/g, "");
        return tagLow.startsWith(q) || labelLow.startsWith(q);
      });
      if (filtered.length === 0) {
        hideAtMention();
        return;
      }
      setAtMention({
        ...am,
        items: filtered,
        activeIdx: am.activeIdx >= filtered.length ? 0 : am.activeIdx,
      });
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    // Block printable character input when at the limit. keydown fires before
    // beforeinput, so preventing it here stops the character before Slate sees it —
    // necessary because slate-react 0.100+ reconciles typed input from the DOM
    // directly, bypassing the editor.insertText plugin for keyboard events.
    if (
      maxLength !== undefined &&
      getPlainLength(editor.children) >= maxLength &&
      e.key.length === 1 &&
      !e.ctrlKey &&
      !e.metaKey
    ) {
      e.preventDefault();
      return;
    }

    // Format hotkeys
    for (const hotkey of Object.keys(HOTKEYS)) {
      if (matchesHotkey(e, hotkey)) {
        e.preventDefault();
        toggleMark(editor, HOTKEYS[hotkey] as TextFormat);
      }
    }

    // @-mention keyboard navigation
    const am = atMentionRef.current;
    if (!am.active || am.items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAtMention({ ...am, activeIdx: (am.activeIdx + 1) % am.items.length });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAtMention({ ...am, activeIdx: (am.activeIdx - 1 + am.items.length) % am.items.length });
    } else if (e.key === "Enter" || e.key === "Tab") {
      const token = am.items[am.activeIdx];
      if (token) {
        e.preventDefault();
        insertAtMentionToken(token);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      hideAtMention();
    }
  }

  // Compute dropdown position relative to wrapperRef using the DOM cursor rect
  function getDropdownStyle(): React.CSSProperties {
    const sel = window.getSelection();
    const wrapper = wrapperRef.current;
    if (!sel || sel.rangeCount === 0 || !wrapper) return { top: "100%", left: 0 };
    const range = sel.getRangeAt(0).cloneRange();
    range.collapse(true);
    const cursorRect = range.getBoundingClientRect();
    const wrapRect = wrapper.getBoundingClientRect();
    if (cursorRect.width === 0 && cursorRect.height === 0) {
      return { top: "100%", left: 0 };
    }
    const top = cursorRect.bottom - wrapRect.top + 4;
    let left = cursorRect.left - wrapRect.left;
    // Clamp so it doesn't overflow the right edge
    const menuWidth = 260;
    const maxLeft = wrapRect.width - menuWidth;
    if (left + menuWidth > wrapRect.width) left = Math.max(0, maxLeft);
    return { top, left };
  }

  const charCount = getPlainLength(editor.children);
  const atLimit = maxLength !== undefined && charCount > maxLength;

  return (
    <div ref={wrapperRef} className="relative">
      <Slate editor={editor} initialValue={initialValue} onChange={handleChange}>
        {/* Toolbar */}
        <div className="flex items-center gap-0.5 px-2 py-1 border border-smoke border-b-0 rounded-t-sm bg-white">
          <MarkButton format="bold">
            <FormatBoldIcon sx={{ fontSize: 16 }} />
          </MarkButton>
          <MarkButton format="italic">
            <FormatItalicIcon sx={{ fontSize: 16 }} />
          </MarkButton>
          <MarkButton format="underline">
            <FormatUnderlinedIcon sx={{ fontSize: 16 }} />
          </MarkButton>
          {showAlign && (
            <>
              <div className="w-px h-4 bg-smoke mx-0.5" />
              <AlignButton align="left">
                <FormatAlignLeftIcon sx={{ fontSize: 16 }} />
              </AlignButton>
              <AlignButton align="center">
                <FormatAlignCenterIcon sx={{ fontSize: 16 }} />
              </AlignButton>
              <AlignButton align="right">
                <FormatAlignRightIcon sx={{ fontSize: 16 }} />
              </AlignButton>
            </>
          )}
        </div>

        {/* Editable area */}
        <Editable
          renderLeaf={renderLeaf}
          renderElement={renderElement}
          placeholder={placeholder}
          spellCheck
          onKeyDown={handleKeyDown}
          onBlur={(e) => {
            if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
              hideAtMention();
            }
          }}
          className={[
            "w-full px-3 py-2.5 border rounded-b-sm bg-white",
            "font-lato text-[16px] text-rodeo-black outline-none",
            "leading-relaxed",
            atLimit ? "border-good-wine" : "border-smoke focus:border-dark-dust",
          ].join(" ")}
          style={{ minHeight }}
        />

        {/* @-mention dropdown */}
        {atMention.active && atMention.items.length > 0 && (
          <TokenMenu
            items={atMention.items}
            activeIdx={atMention.activeIdx}
            onInsert={insertAtMentionToken}
            className="absolute z-50"
            style={getDropdownStyle()}
          />
        )}
      </Slate>

      {/* Char count */}
      {maxLength !== undefined && (
        <div className="flex justify-end mt-1">
          <span className={`font-lato text-[11px] ${atLimit ? "text-good-wine" : "text-graphite"}`}>
            {charCount} / {maxLength}
          </span>
        </div>
      )}
    </div>
  );
});

export default memo(RichEditor);
